import { useAtomValue } from 'jotai';
import { Suspense, useContext, useEffect, useState } from 'react';
import { Box, Flex, Select, SelectContent, SelectTrigger, Tooltip } from '@optiaxiom/react';
import { useFeature } from '@optimizely/react-sdk';

import { TurnstileInstance } from '@frontend/api/types';
import { opalAuthAtom } from '@frontend/atoms';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import chatConfig from '@frontend/config/chat';
import { ChatConnectionContext } from '@frontend/contexts';
import { OPAL_URL, SEGMENT_WRITE_KEY } from '@frontend/envConfig';
import { useOpalUser } from '@frontend/hooks/useOpalUser';
import { useThread } from '@frontend/hooks/useThread';
import { OPAL_ANALYTICS_STORAGE_KEY, OPAL_APP_BASE_URL_STORAGE_KEY } from '@frontend/utils/misc';

import { useSidebarVisibility } from '../hooks/useSidebarVisibility';

import { chatInstanceSelectionStyle, chatRootElementStyle } from './Chat.css';
import { ChatEventListener } from './ChatEventListener';
import { LazyAIChatComponent } from './LazyAiChatComponent';

export function Chat() {
  const { opalUser } = useOpalUser();
  const { products } = useContext(ChatConnectionContext);
  const { threadId } = useThread();
  const opalAuth = useAtomValue(opalAuthAtom);
  const [chatFromFF, primaryAgentConfig] = useFeature('opal_app_features');
  const chatConfigFromFF = chatFromFF ? primaryAgentConfig.chat_initial_prompt : {};

  const [overrideInstance, setOverrideInstance] = useState<TurnstileInstance>();
  const { isSidebarVisibleOnMobile } = useSidebarVisibility();

  const setOverrideInstanceId = (instanceId: string) => {
    const selectedInstance = Object.values(products)
      .flatMap(group => group.instances)
      .find(instance => instance.id === instanceId);
    setOverrideInstance(selectedInstance);
  };

  useEffect(() => {
    if (SEGMENT_WRITE_KEY) {
      const analyticsStorage = localStorage.getItem(OPAL_ANALYTICS_STORAGE_KEY);
      if (!analyticsStorage) {
        localStorage.setItem(OPAL_ANALYTICS_STORAGE_KEY, SEGMENT_WRITE_KEY);
      }
      if (OPAL_URL) {
        localStorage.setItem(OPAL_APP_BASE_URL_STORAGE_KEY, OPAL_URL);
      }
    }
  }, []);

  const groups = Object.keys(products).reduce(
    (acc, label) => {
      acc[label] = {
        label,
        separator: true,
      };
      return acc;
    },
    {} as Record<string, { label: string; separator: boolean }>
  );

  const instanceSelectOptions = Object.entries(products).flatMap(([groupLabel, groupItems]) => {
    return groupItems.instances.map(instance => ({
      group: groups[groupLabel],
      label: instance.nickname ?? instance.name,
      value: instance.id,
    }));
  });

  return (
    <Flex h="full" flex="1" w="full" overflow="hidden" className={chatRootElementStyle}>
      {!isSidebarVisibleOnMobile && (
        <Tooltip
          content="Some Opal capabilities depend on specific Optimizely products.
          Switch here for Opal to take action in the selected product."
        >
          <Box className={chatInstanceSelectionStyle}>
            <Select
              options={instanceSelectOptions}
              onValueChange={setOverrideInstanceId}
              value={overrideInstance?.id}
            >
              <SelectTrigger placeholder="Set Product Instance" w="fit" />
              <SelectContent />
            </Select>
          </Box>
        </Tooltip>
      )}
      <Suspense fallback={<FullScreenLoader />}>
        {Boolean(opalAuth) && (
          <ChatEventListener>
            <LazyAIChatComponent
              currentUser={{
                instanceId: overrideInstance?.id ?? opalUser.instance.instance_id,
                avatarUrl: '',
                name: opalUser.firstname + ' ' + opalUser.lastname,
                operations: opalUser.instance.operations
                  .filter(op => op.allowed)
                  .map(op => op.name),
              }}
              initialThreadId={threadId}
              opalAuth={opalAuth}
              storeKey="general"
              productScope={overrideInstance?.product_sku ?? 'OPAL'}
              showHeaderIcons={false}
              sidebarAlwaysVisible={false}
              // To test how Opal looks in other products, make this initialDockState="popout"
              // There's also the fullscreen mode initialDockState="fullscreen"
              initialDockState="docked"
              initialPrompt={chatConfigFromFF.initialPrompt || chatConfig.initialPrompt}
              initialSuggestions={chatConfigFromFF.initialSuggestions || []}
            />
          </ChatEventListener>
        )}
      </Suspense>
    </Flex>
  );
}
